var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { TweenMax } from 'gsap';
import { ActivityManagerTyped as ActivityManager } from 'mixins/ActivityManager';
import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityStage from 'components/activity/ActivityStage.vue';
import ActivityQuestion from 'components/activity/ActivityQuestion.vue';
import ActivityNextSectionPortal from '../components/activity/ActivityNextSectionPortal.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import HtmlRenderer from 'components/activity/HtmlRenderer.vue';
import MultiSelectCheckBoxes from 'components/activity/MultiSelectCheckBoxes.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';
import ActivityWrapper from '../components/activity/ActivityWrapper.vue';
import mixins from 'vue-typed-mixins';
const HINT = 'HINT';
const INCORRECT_ATTEMPT = 'INCORRECT_ATTEMPT';
const TYPE_OVER_TEXT = 'TYPE_OVER_TEXT';
export default mixins(AssetLoader, TypingActivityHelper, ActivityManager(), ErrorLogger).extend({
    components: {
        ActivityStage,
        BlinkingCursorText,
        TextDisplayList,
        MultiSelectCheckBoxes,
        ActivityQuestion,
        HtmlRenderer,
        ActivityNextSectionPortal,
        ActivityWrapper
    },
    data() {
        return {
            activityStatus: {
                showErrors: false,
                showHints: false
            },
            hasCompletedMultiSelect: false,
            lockedCheckBoxSelections: [],
            selectedIds: [],
            audiosMap: {}
        };
    },
    computed: {
        areAllSelectedAnswersCorrect() {
            const correctAnswers = this.currentRound.answers.filter(answer => answer.correct);
            const areAllSelectedCorrect = this.selectedIds.every(selectedId => correctAnswers.some(correctAnswer => correctAnswer.id === selectedId));
            return !!(this.selectedIds.length &&
                areAllSelectedCorrect &&
                this.selectedIds.length === correctAnswers.length);
        },
        correctSelectedMultiSelectAnswers() {
            return this.selectedIds.filter(selectedId => !!this.currentRound.answers.find(answer => answer.correct && answer.id === selectedId));
        }
    },
    methods: {
        // start activity helper methods
        getActivityState() {
            const { rounds, incorrectAttempt } = this.activityData;
            return {
                rounds,
                incorrectAttempt,
                instructions: {
                    written: rounds[0].instructions.audio.description,
                    voice: rounds[0].instructions.audio.src
                }
            };
        },
        loadActivity() {
            return __awaiter(this, void 0, void 0, function* () {
                const roundSetup = this.getActivityState();
                this.createRounds(roundSetup);
                yield this.loadImages();
                this.audiosMap = {
                    [INCORRECT_ATTEMPT]: roundSetup.incorrectAttempt.audio.src,
                    [HINT]: roundSetup.rounds[0].hint.audio.src,
                    [TYPE_OVER_TEXT]: roundSetup.rounds[0].typeOverText.instructions.audio.src
                };
                const audioDescriptionsMap = {
                    [INCORRECT_ATTEMPT]: roundSetup.incorrectAttempt.audio.description,
                    [HINT]: roundSetup.rounds[0].hint.audio.description,
                    [TYPE_OVER_TEXT]: roundSetup.rounds[0].typeOverText.instructions.audio.description
                };
                this.sfx = yield this.loadAudios(this.audiosMap);
                this.sfxDescriptions = audioDescriptionsMap;
            });
        },
        loadAudios(audiosMap) {
            return __awaiter(this, void 0, void 0, function* () {
                const soundPaths = Object.keys(audiosMap).map(key => audiosMap[key]);
                this.queueAudio(soundPaths);
                const cache = yield this.downloadAudio();
                const cachedAudiosMap = {};
                Object.keys(audiosMap).forEach(key => (cachedAudiosMap[key] = cache.get(audiosMap[key])));
                return cachedAudiosMap;
            });
        },
        loadImages() {
            return __awaiter(this, void 0, void 0, function* () {
                this.rounds.forEach(({ image }) => {
                    this.queueImage(image);
                });
                return this.downloadImages();
            });
        },
        getKeyboardConfig() {
            const { keyboardType, roofs, showNumbers } = this.activityData.keyboard;
            return {
                keyboardType,
                hideNumberRow: !showNumbers,
                showKeyboardAtStart: false,
                roofs
            };
        },
        startActivity() {
            this.setRound();
            this.$nextTick(this.setActivityContainerHeight);
        },
        setRound() {
            this.setupNewRound();
            this.errorLoggerStart();
            this.$nextTick(() => {
                TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
            });
        },
        handleCorrectKey(attempt) {
            this.incrementTypingIndex();
            this.sndType();
            this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);
            if (this.isEndOfCurrentSentence) {
                this.sndBell();
                this.setCurrentStep();
            }
            if (this.isEndOfRound) {
                this.sndBell();
                this.processEndOfActivity();
            }
        },
        // end activity helper methods
        // start activity specific methods
        handleAllAnswersCorrect() {
            this.setErrorMessage('');
            this.hasCompletedMultiSelect = true;
            TweenMax.delayedCall(1.5, () => {
                var _a;
                this.overwriteProgressText({
                    currentText: this.currentRound.typeOverText.text
                });
                this.enableKeyboard();
                this.showKeyboard();
                this.$nextTick(this.setActivityContainerHeight);
                this.setInstructions({
                    voice: this.audiosMap[TYPE_OVER_TEXT],
                    written: this.sfxDescriptions[TYPE_OVER_TEXT]
                });
                const container = (_a = document.getElementsByClassName('is-chrome-ipad')) === null || _a === void 0 ? void 0 : _a[0];
                container === null || container === void 0 ? void 0 : container.scrollTo(0, 0);
            });
        },
        handleOnCheckBoxesSelectionChange(checkBoxes) {
            this.selectedIds = checkBoxes;
        },
        handleOnActivitySubmit() {
            if (this.hasCompletedMultiSelect) {
                return;
            }
            this.incrementNumberOfAttempts();
            if (this.areAllSelectedAnswersCorrect) {
                this.handleAllAnswersCorrect();
            }
            else {
                this.handleWrongGuess();
            }
            this.lockedCheckBoxSelections = this.correctSelectedMultiSelectAnswers;
        },
        handleWrongGuess() {
            this.setErrorMessage('Try again');
            this.activityStatus.showErrors = true;
            this.activityStatus.showHints = false;
            if (this.numberOfAttempts[0] <= 1)
                this.playAudio(INCORRECT_ATTEMPT);
            TweenMax.delayedCall(1.5, () => {
                this.activityStatus.showErrors = false;
                if (this.numberOfAttempts[0] > 1) {
                    this.activityStatus.showHints = true;
                    this.promptScreenReaderHintAlert();
                    this.playAudio(HINT);
                }
            });
        },
        setErrorMessage(message) {
            const errorMessageEl = document.getElementById('multiSelectErrorMessage');
            if (errorMessageEl)
                errorMessageEl.innerText = message;
        },
        promptScreenReaderHintAlert() {
            const correctAnswers = [];
            const alphaIndexMap = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
            this.currentRound.answers.forEach((answer, index) => {
                if (answer.correct) {
                    correctAnswers.push({
                        index: index,
                        label: answer.label
                    });
                }
            });
            this.rerenderScreenReaderAlert(`The correct answers are items ${correctAnswers
                .map(({ index, label }) => `${alphaIndexMap[index]}. ${label}`)
                .join(' and ')}.`);
        }
    }
});
